import {
  Box,
  Stack,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StorageKeys } from "../../../services/constants/StorageKeys";
import { storage } from "../../../services/storageService";
import Page from '../../../components/Page';
import useConfirmBooking from "../api/useConfirmBooking";
import usePreReserve from "../api/usePreReserve";
import Article from "../../../components/Article";
import CustomButton from "../../../components/CustomButton";
import SupplySummary from "../components/SupplySummary";
import CouponField from "../components/CouponField";
import BorderedBox from "../../../components/BorderedBox";
import { Routes } from "../../../routes/RouteConstants";
import { gradientTextSx, tint } from "../../../utils/textStyles";
import { useTenant } from "../../../contexts/TenantContext";
import useProviderChoice from "../../provider/hooks/useProviderChoice";
import { findProvider } from "../../provider/providerChoice";
import ProviderAvatar from "../../provider/components/ProviderAvatar";

/** One line of the "Jó tudni" box: an icon in the tenant's colour, then the text. */
const GoodToKnowRow = ({ icon: Icon, accent, testId, children }) => (
  <Box display="grid" gridTemplateColumns="auto 1fr" columnGap={1} alignItems="start">
    <Icon sx={{ fontSize: 18, color: accent, mt: "1px" }} />
    <Typography fontSize={13.5} data-testid={testId}>{children}</Typography>
  </Box>
);

const ConfirmBookingLayout = () => {
  const { loading, confirmBooking } = useConfirmBooking();
  const { loading: preReserving, preReserve } = usePreReserve();
  const navigate = useNavigate();
  const { displayConfig, cancellationDeadlineHours, isCouponEnabled, hasActiveCoupon, providers, hasSeveralProviders } = useTenant();
  const { providerId: chosenProviderId } = useProviderChoice();
  // The coupon the guest redeemed here, as the check priced it; sent along on "Véglegesítés".
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const selectedSupply = storage.get(StorageKeys.SELECTED_SUPPLY);
  const selectedAppointment = storage.get(StorageKeys.SELECTED_APPOINTMENT);
  // Who serves the slot: the booking page's hold says, and back from signing in this page's own does.
  const [assignedProviderId, setAssignedProviderId] = useState(selectedAppointment.providerId);
  const assignedProvider = findProvider(providers, assignedProviderId);
  const accent = displayConfig.primaryColor;
  const notice = displayConfig?.confirmBookingPage?.notice;
  const hasCancellationDeadline = cancellationDeadlineHours > 0;

  // Back from signing in, the guest has no pre-reservation yet. The page makes it as it opens,
  // so the guest never reads about a slot someone else can still take: a deliberate exception
  // to "no write on page load", made safe by the backend returning the same hold for the same
  // slot (DECISIONS.md D26). The button stays as a retry if this attempt failed.
  //
  // The hold is asked for again every time, never remembered. An id kept from before could
  // outlive its hold, which expires after 5 minutes, or belong to an earlier choice. The backend
  // answers the current choice with the hold the guest already has for it, a fresh one, or one
  // that replaces the guest's hold on another slot.
  //
  // With several providers the hold names the provider the slot went to, also for „Bárki”: asking
  // „Bárki” again could draw someone other than the provider this page names
  // (docs/MULTI_PROVIDER.md). If that provider has been taken since, the slot is lost as any other.
  const ensurePreReservation = async () => {
    const appointment = storage.get(StorageKeys.SELECTED_APPOINTMENT);
    const booking = await preReserve({
      supplyId: selectedSupply.id,
      date: appointment.date,
      startTime: appointment.time,
      providerId: hasSeveralProviders ? appointment.providerId ?? chosenProviderId : undefined,
    });
    if (booking) {
      storage.set(StorageKeys.SELECTED_APPOINTMENT, { ...appointment, providerId: booking.providerId });
      setAssignedProviderId(booking.providerId);
    }
    return booking;
  };

  useEffect(() => {
    ensurePreReservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBookingConfirmation = async () => {
    const preReservedBooking = await ensurePreReservation();
    if (!preReservedBooking) return;

    // A coupon refused here (it expired since the check, say) refuses the confirmation too, so the
    // guest is never booked at a price they did not see. The coupon comes off the page; the next
    // click books at the full price, which the summary then shows.
    const confirmedBooking = await confirmBooking(
      preReservedBooking.id, appliedCoupon?.couponCode, () => setAppliedCoupon(null));
    if (!confirmedBooking) return;
    
    // The next booking starts from the choice of provider again (docs/MULTI_PROVIDER.md).
    storage.remove(StorageKeys.SELECTED_PROVIDER);
    storage.remove(StorageKeys.SELECTED_SUPPLY);
    storage.remove(StorageKeys.SELECTED_APPOINTMENT);
    // The success page shows the price the booking was made at.
    navigate(confirmedBooking.status === "BOOKED" ? Routes.BOOKING_SUCCESS : Routes.PENDING_APPROVAL,
      { state: { booking: confirmedBooking } });
  };

  return (
    <Page>
      <Article title="Foglalás véglegesítése" titleVariant="h5">
        <Stack spacing={3} minWidth="100%" mt={2} mb={5}>
          <SupplySummary title="Szolgáltatás" supply={selectedSupply} coupon={appliedCoupon} />

          {/* Only while the tenant has a coupon to redeem today: everywhere else there is no field
              to wonder about. */}
          {isCouponEnabled && hasActiveCoupon && (
            <CouponField
              supplyId={selectedSupply.id}
              appliedCoupon={appliedCoupon}
              onApply={setAppliedCoupon}
              onRemove={() => setAppliedCoupon(null)}
            />
          )}

          {/* The full name: the guest is about to commit to this person. „Bárki” only until the hold
              has drawn someone. */}
          {hasSeveralProviders && (
            <BorderedBox title={displayConfig.providerLabel}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <ProviderAvatar provider={assignedProvider} size={36} />
                <Typography fontWeight="bold" color={displayConfig.color} data-testid="confirm-booking-provider"
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {assignedProvider?.name ?? "Bárki"}
                </Typography>
              </Stack>
            </BorderedBox>
          )}

          <BorderedBox title = "Időpont">
            <Typography fontWeight="bold" sx={gradientTextSx(displayConfig, displayConfig.primaryColor)}>{selectedAppointment.date.replaceAll("-", ".")}</Typography>
            <Typography color={displayConfig.color}>{selectedAppointment.weekday}</Typography>
            <Typography color={displayConfig.color}>{selectedAppointment.time}</Typography>
          </BorderedBox>

          {/* What the guest should know before committing, in one box (DECISIONS.md D31): the
              cancellation deadline, told now rather than first when they try to cancel (D28), and
              the tenant's own warning from its display config (D29). Neither, no box. */}
          {(hasCancellationDeadline || notice) && (
            <Stack
              spacing={1}
              data-testid="good-to-know"
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: `1px solid ${tint(accent, 0.4)}`,
                backgroundColor: tint(accent, 0.1),
                color: displayConfig.color,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <InfoOutlinedIcon sx={{ fontSize: 18, color: accent }} />
                <Typography fontSize={14} fontWeight={700}>Jó tudni</Typography>
              </Stack>
              {hasCancellationDeadline && (
                <GoodToKnowRow icon={EventBusyIcon} accent={accent}>
                  {`A foglalást legkésőbb ${cancellationDeadlineHours} órával a kezdés előtt mondhatod le.`}
                </GoodToKnowRow>
              )}
              {notice && (
                <GoodToKnowRow icon={ReportProblemOutlinedIcon} accent={accent} testId="confirm-booking-notice">
                  {notice}
                </GoodToKnowRow>
              )}
            </Stack>
          )}
        </Stack>

        {/* As wide as the wider of the two, which both then fill, so they stand the same width; and
            never narrower than a comfortable target for a thumb. */}
        <Stack sx={{ width: "fit-content", minWidth: "min(100%, 220px)", mx: "auto" }}>
          {/* The page stays while the click runs: the button carries both steps, the hold and the
              confirmation. */}
          <CustomButton text="Véglegesítés" isLoading={preReserving || loading} onClick={handleBookingConfirmation}
            width="100%" />
          {/* Back to the slots; the hold stays until it expires or the guest picks another. Not while
              the confirmation runs, which would land on its result page anyway. */}
          <CustomButton
            variant="outlined"
            text="Vissza"
            disabled={loading}
            onClick={() => navigate(Routes.BOOKING)}
            width="100%"
            mt={1.5}
          />
        </Stack>
      </Article>
    </Page> 
  );
};

export default ConfirmBookingLayout;
